import { useEffect, useMemo, useState } from "react";
import { Flag, Crown, Swords, Gift, Sparkles, ShieldCheck, ArrowUpRight } from "lucide-react";
import { trackEvent } from "@/lib/audit";
import { useSelectedNation } from "@/hooks/useSelectedNation";
import {
  ambassadorScore,
  ambassadorTier,
  ambassadorTierInfo,
  bumpAmbassador,
  getAmbassadorState,
  nextTierProgress,
} from "@/lib/nationalPride/ambassador";
import { getAscentSnapshot, ascentSteps } from "@/lib/nationalPride/nationalAscent";
import { nationMeta, shouldShowImpact, markImpactShown } from "@/lib/nationalPride/nationImpact";
import { listActiveRivalries, rivalryDisplay } from "@/lib/nationalPride/activeRivalries";
import {
  bumpNationalReward,
  claimNationalReward,
  listNationalRewards,
} from "@/lib/nationalPride/socialRewards";

/**
 * SPRINT V2 — Orgullo Nacional surface. Composes the 5 emotional modules
 * into a single hub mounted inside the home feed drawer. Pure presentation,
 * client-only state, no backend wiring.
 */
export function NationalPrideSprintV2Hub() {
  const { nation } = useSelectedNation();
  const [tick, setTick] = useState(0);
  const refresh = () => setTick((x) => x + 1);

  useEffect(() => {
    void trackEvent("national_pride_hub_viewed", { nation });
  }, [nation]);

  if (!nation) {
    return (
      <div className="rounded-2xl border border-border/60 bg-background/40 p-4 text-center">
        <Flag className="mx-auto h-5 w-5 text-primary" />
        <div className="mt-2 text-sm font-bold">Elige tu nación</div>
        <div className="text-[11px] text-muted-foreground">
          Desbloquea Embajador Nacional, ascenso visual y rivalidades activas.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <NationImpactCard nation={nation} />
      <NationalAmbassadorCard nation={nation} onChange={refresh} tick={tick} />
      <NationalAscentCard nation={nation} tick={tick} />
      <ActiveRivalriesCard nation={nation} onDefend={refresh} />
      <SocialRewardsCard nation={nation} onChange={refresh} tick={tick} />
    </div>
  );
}

function Card({ children, accent }: { children: React.ReactNode; accent?: string }) {
  return (
    <div
      className="rounded-xl border border-border/60 bg-background/40 p-3"
      style={accent ? { boxShadow: `inset 0 0 0 1px ${accent}` } : undefined}
    >
      {children}
    </div>
  );
}

function NationImpactCard({ nation }: { nation: string }) {
  const meta = nationMeta(nation)!;
  const [show, setShow] = useState<boolean>(() => shouldShowImpact(nation));
  useEffect(() => {
    if (show) {
      markImpactShown(nation);
      void trackEvent("national_pride_impact_shown", { nation });
    }
  }, [show, nation]);
  if (!show) return null;
  return (
    <Card accent={`${meta.accent}66`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="text-3xl" aria-hidden>{meta.flag}</div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Has elegido
            </div>
            <div className="text-lg font-black" style={{ color: meta.accent }}>
              {meta.cry}
            </div>
            <div className="text-[11px] text-muted-foreground">
              Ahora representas a {meta.name}.
            </div>
          </div>
        </div>
        <button
          onClick={() => setShow(false)}
          className="rounded-md border border-border/60 px-2 py-1 text-[10px] font-bold uppercase"
        >
          Listo
        </button>
      </div>
    </Card>
  );
}

function NationalAmbassadorCard({
  nation,
  onChange,
  tick,
}: { nation: string; onChange: () => void; tick: number }) {
  const state = useMemo(() => getAmbassadorState(nation), [nation, tick]);
  const tier = ambassadorTier(state);
  const info = ambassadorTierInfo(tier);
  const score = ambassadorScore(state);
  const { pct, remaining, nextThreshold } = nextTierProgress(state);
  const meta = nationMeta(nation)!;

  useEffect(() => {
    void trackEvent("national_pride_ambassador_viewed", { nation, tier, score });
  }, [nation, tier, score]);

  return (
    <Card accent={`${info.glow}55`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Crown className="h-4 w-4" style={{ color: info.glow }} />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Embajador {meta.flag}
            </div>
            <div className="text-sm font-black">
              {info.emoji} {info.label}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-black tabular-nums" style={{ color: info.glow }}>
            {score}
          </div>
          <div className="text-[10px] text-muted-foreground">Pts nacionales</div>
        </div>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${Math.round(pct * 100)}%`, background: info.glow }}
        />
      </div>
      <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
        <span>↗ {state.shares} · 🔮 {state.predictions} · 🔥 {state.hype} · 🛡️ {state.defenses}</span>
        {nextThreshold ? <span>{remaining} para subir</span> : <span>Tier máximo</span>}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <QuickAction
          label="🔥 +Hype"
          onClick={() => {
            bumpAmbassador("hype", 3, nation);
            bumpNationalReward("hype", 3);
            void trackEvent("national_pride_action_executed", { nation, action: "hype" });
            onChange();
          }}
        />
        <QuickAction
          label="🔮 +Predicción"
          onClick={() => {
            bumpAmbassador("predictions", 1, nation);
            bumpNationalReward("predict", 1);
            void trackEvent("national_pride_action_executed", { nation, action: "predict" });
            onChange();
          }}
        />
        <QuickAction
          label="↗ Compartir"
          onClick={() => {
            bumpAmbassador("shares", 1, nation);
            bumpNationalReward("share", 1);
            void trackEvent("national_pride_action_executed", { nation, action: "share" });
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("ss2026:open-share-sheet"));
            }
            onChange();
          }}
        />
      </div>
    </Card>
  );
}

function QuickAction({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="rounded-full border border-border/60 bg-background/60 px-2 py-1 text-[10px] font-bold hover:border-primary/60"
    >
      {label}
    </button>
  );
}

function NationalAscentCard({ nation, tick }: { nation: string; tick: number }) {
  const snap = useMemo(() => getAscentSnapshot(nation), [nation, tick]);
  const steps = ascentSteps();
  useEffect(() => {
    void trackEvent("national_ascent_viewed", { nation, step: snap.step });
  }, [nation, snap.step]);
  return (
    <Card accent={`${snap.glow}55`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ArrowUpRight className="h-4 w-4" style={{ color: snap.glow }} />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Ascenso nacional
            </div>
            <div className="text-sm font-black">
              {snap.emoji} {snap.label}
            </div>
          </div>
        </div>
        <div className="text-[10px] text-muted-foreground">
          {snap.stepIndex + 1}/{snap.totalSteps}
        </div>
      </div>
      <div className="mt-2 grid grid-cols-5 gap-1">
        {steps.map((s, i) => {
          const reached = i <= snap.stepIndex;
          return (
            <div
              key={s.step}
              className="flex flex-col items-center gap-1 rounded-md px-1 py-1 text-center"
              style={{
                background: reached ? `${s.glow}1a` : "transparent",
                boxShadow: i === snap.stepIndex ? `0 0 0 1px ${s.glow}` : undefined,
              }}
            >
              <span className="text-base" aria-hidden style={{ opacity: reached ? 1 : 0.3 }}>{s.emoji}</span>
              <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: reached ? s.glow : undefined }}>
                {s.label.split(" ")[0]}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-2 h-1 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full" style={{ width: `${Math.round(snap.pctToNext * 100)}%`, background: snap.glow }} />
      </div>
    </Card>
  );
}

function ActiveRivalriesCard({ nation, onDefend }: { nation: string; onDefend: () => void }) {
  const rows = useMemo(() => listActiveRivalries(nation).slice(0, 3), [nation]);
  useEffect(() => {
    void trackEvent("active_rivalries_viewed", { nation, count: rows.length });
  }, [nation, rows.length]);

  return (
    <Card accent="#ef444455">
      <div className="flex items-center gap-2">
        <Swords className="h-4 w-4 text-[hsl(0_84%_60%)]" />
        <div className="text-sm font-black">Rivalidades activas</div>
      </div>
      <ul className="mt-2 space-y-2">
        {rows.map((r) => {
          const d = rivalryDisplay(r);
          const involved = r.left === nation || r.right === nation;
          return (
            <li key={r.id} className="rounded-lg border border-border/40 bg-background/40 p-2">
              <div className="flex items-center justify-between text-xs font-bold">
                <span>{d.leftMeta.flag} {d.leftMeta.name}</span>
                <span className="text-[10px] text-muted-foreground">vs</span>
                <span>{d.rightMeta.name} {d.rightMeta.flag}</span>
              </div>
              <div className="mt-1 flex h-1.5 overflow-hidden rounded-full bg-muted">
                <div style={{ width: `${d.leftPct}%`, background: d.leftMeta.accent }} />
                <div style={{ width: `${d.rightPct}%`, background: d.rightMeta.accent }} />
              </div>
              <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
                <span>{d.leftPct}%</span>
                <span className={involved ? "font-black text-foreground" : ""}>{r.label}</span>
                <span>{d.rightPct}%</span>
              </div>
              {involved && (
                <button
                  onClick={() => {
                    bumpAmbassador("defenses", 1, nation);
                    bumpNationalReward("defend", 1);
                    void trackEvent("national_rivalry_defended", { nation, rivalry: r.id });
                    onDefend();
                  }}
                  className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-md bg-[hsl(0_84%_60%)] px-2 py-1 text-[10px] font-bold text-white"
                >
                  <ShieldCheck className="h-3 w-3" /> Defender mi nación
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function SocialRewardsCard({
  nation,
  onChange,
  tick,
}: { nation: string; onChange: () => void; tick: number }) {
  const rows = useMemo(() => listNationalRewards(), [tick]);
  useEffect(() => {
    void trackEvent("national_social_rewards_viewed", { nation, count: rows.length });
  }, [nation, rows.length]);
  return (
    <Card accent="#a855f755">
      <div className="flex items-center gap-2">
        <Gift className="h-4 w-4 text-[hsl(270_80%_65%)]" />
        <div className="text-sm font-black">Recompensas nacionales</div>
        <Sparkles className="ml-auto h-3 w-3 text-muted-foreground" />
      </div>
      <ul className="mt-2 space-y-1.5">
        {rows.map((r) => {
          const pct = Math.min(1, r.progress / r.goal);
          const ready = r.progress >= r.goal && !r.claimed;
          return (
            <li key={r.id} className="rounded-lg border border-border/40 bg-background/40 p-2">
              <div className="flex items-center justify-between text-xs font-bold">
                <span>{r.emoji} {r.title}</span>
                <span className="text-[10px] text-muted-foreground">+{r.reward} pts</span>
              </div>
              <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-[hsl(270_80%_65%)]" style={{ width: `${Math.round(pct * 100)}%` }} />
              </div>
              <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
                <span>{r.progress}/{r.goal}</span>
                {r.claimed ? (
                  <span className="font-bold text-[hsl(140_70%_55%)]">Reclamado ✓</span>
                ) : ready ? (
                  <button
                    onClick={() => {
                      const res = claimNationalReward(r.id);
                      if (res.ok) {
                        void trackEvent("national_social_reward_claimed", { nation, id: r.id, reward: res.reward });
                        onChange();
                      }
                    }}
                    className="rounded-md bg-[hsl(270_80%_65%)] px-2 py-0.5 text-[10px] font-bold text-white"
                  >
                    Reclamar
                  </button>
                ) : (
                  <span>Sigue defendiendo</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}